// File: frontend/src/components/ads/AdSlot.tsx

import React, { useEffect, useRef } from "react";
import { useCookieConsent } from "../../context/CookieConsentContext";
import "./AdSlot.css";

/**
 * A single Google AdSense unit.
 *
 * Three things this component has to get right, each of which breaks in a way
 * that is easy to miss:
 *
 * 1. CONSENT. The AdSense script is not loaded at all until the visitor has
 *    accepted advertising cookies. Loading it first and asking later would make
 *    the cookie banner a lie — the script sets identifiers the moment it runs.
 *
 * 2. DOUBLE PUSH. React StrictMode runs effects twice in development. Pushing
 *    the same slot twice makes AdSense throw "All 'ins' elements in the DOM with
 *    class=adsbygoogle already have ads in them". The pushedRef guard makes the
 *    push happen exactly once per mounted slot.
 *
 * 3. LAYOUT SHIFT. The slot reserves its height up front via CSS. Without that,
 *    the page jumps when an ad fills, which is both unpleasant and penalised by
 *    Core Web Vitals.
 *
 * When there is no ad to show — REACT_APP_ADSENSE_CLIENT unset, or the visitor
 * declined advertising cookies — the component renders NOTHING in production.
 * A visible "ad space" box is worse than an empty column: it advertises the
 * advertising, and it tells someone who just declined tracking that they are
 * being shown a hole where a tracker would be.
 *
 * In development it renders a dashed placeholder instead, so the layout can be
 * checked without Google being involved at all. AdSense does not serve ads to
 * localhost in any case.
 */

declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT;

// Tracks whether the loader <script> has been appended, so that a page with
// several AdSlot components still only injects it once.
let scriptInjected = false;

const injectAdSenseScript = (): void => {
  if (scriptInjected || !ADSENSE_CLIENT) return;
  if (document.querySelector('script[data-3k1p-adsense="1"]')) {
    scriptInjected = true;
    return;
  }

  const script = document.createElement("script");
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  script.async = true;
  script.crossOrigin = "anonymous";
  script.setAttribute("data-3k1p-adsense", "1");
  document.head.appendChild(script);
  scriptInjected = true;
};

interface AdSlotProps {
  /** AdSense ad unit id — the data-ad-slot value, digits only. */
  slot: string;
  /** Ad unit format. "auto" lets AdSense pick; "vertical" suits a sidebar. */
  format?: string;
  /** Reserved height in pixels. Must match what the unit will actually render. */
  height?: number;
  /** Extra class on the wrapper, for per-placement styling. */
  className?: string;
  /** Label shown on the development-only placeholder. */
  placeholderLabel?: string;
}

const AdSlotComponent: React.FC<AdSlotProps> = ({
  slot,
  format = "auto",
  height = 600,
  className = "",
  placeholderLabel = "Ad slot",
}) => {
  const { consent } = useCookieConsent();
  const pushedRef = useRef(false);
  const insRef = useRef<HTMLModElement>(null);

  const adsAllowed = consent.ads === true;
  const configured = Boolean(ADSENSE_CLIENT && slot);

  useEffect(() => {
    if (!configured || !adsAllowed || pushedRef.current) return;

    injectAdSenseScript();

    try {
      // adsbygoogle may not exist yet if the loader script is still in flight.
      // Pushing onto the array before it loads is the documented pattern: the
      // script drains whatever it finds there once it is ready.
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
      pushedRef.current = true;
    } catch (err) {
      // A failed ad must never take the page down with it.
      console.warn("[AdSlot] push failed", err);
    }
  }, [configured, adsAllowed]);

  // Nothing to show: either ads are not configured for this build, or the
  // visitor declined advertising cookies.
  if (!configured || !adsAllowed) {
    // Production renders nothing at all — no box, no reserved height, no text.
    if (process.env.NODE_ENV !== "development") return null;

    // Development keeps a labelled outline so the placement stays visible
    // while working on the layout.
    return (
      <div
        className={`ad-slot ad-slot--placeholder ${className}`}
        style={{ minHeight: height }}
      >
        <span className="ad-slot__placeholder-text">
          {!adsAllowed ? `${placeholderLabel} — consent denied` : placeholderLabel}
        </span>
      </div>
    );
  }

  return (
    <div className={`ad-slot ${className}`} style={{ minHeight: height }}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", minHeight: height }}
        data-ad-client={ADSENSE_CLIENT}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export const AdSlot = React.memo(AdSlotComponent);